import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Modal,
  Input,
  Alert,
  Badge,
  Tooltip,
  Descriptions,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckSquareOutlined,
  DollarOutlined,
  TeamOutlined,
  LockOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector as useSel } from 'react-redux';
import { hasPermission } from '../../utils/permissions';
import {
  fetchPendingApprovalsRequest,
  approvePORequest,
  rejectPOApprovalRequest,
} from '../../redux/actions/approvalActions';

const { Text, Title } = Typography;
const { TextArea } = Input;

const fmtMoney = (val) =>
  val != null
    ? `₹ ${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    : '—';

const fmtDate = (val) =>
  val ? dayjs(val).format('DD MMM YYYY, hh:mm A') : '—';

// Reusable approval status badge
export const getApprovalStatusTag = (status) => {
  const map = {
    pending_approval: { color: 'gold',    icon: <ClockCircleOutlined />,  text: 'Pending Approval' },
    approved:         { color: 'success', icon: <CheckCircleOutlined />,  text: 'Approved'         },
    rejected:         { color: 'error',   icon: <CloseCircleOutlined />,  text: 'Rejected'         },
  };
  const cfg = map[status] || { color: 'default', icon: null, text: status || '—' };
  return (
    <Tag color={cfg.color} icon={cfg.icon} style={{ fontWeight: 500 }}>
      {cfg.text}
    </Tag>
  );
};

const ApprovalInbox = () => {
  const dispatch = useDispatch();
  const permissions = useSel((s) => s.auth.permissions || []);
  const { pendingApprovals, loading, actionLoading, error } = useSelector(
    (s) => s.approvals
  );

  const canApprove = hasPermission(permissions, 'purchase_order/update');

  const [approveModal, setApproveModal] = useState(null); // { poId, poNumber, amount }
  const [rejectModal, setRejectModal]   = useState(null);
  const [approveRemarks, setApproveRemarks]   = useState('');
  const [rejectReason, setRejectReason]       = useState('');
  const [rejectError, setRejectError]         = useState('');

  useEffect(() => {
    if (canApprove) dispatch(fetchPendingApprovalsRequest());
  }, [canApprove, dispatch]);

  const handleApprove = () => {
    if (!approveModal) return;
    dispatch(approvePORequest(approveModal.poId, { remarks: approveRemarks }));
    setApproveModal(null);
    setApproveRemarks('');
  };

  const handleReject = () => {
    if (!rejectModal) return;
    if (!rejectReason.trim()) {
      setRejectError('Rejection reason is required.');
      return;
    }
    dispatch(rejectPOApprovalRequest(rejectModal.poId, { rejection_reason: rejectReason }));
    setRejectModal(null);
    setRejectReason('');
    setRejectError('');
  };

  if (!canApprove) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-state-icon"><LockOutlined /></div>
          <div className="empty-state-text">You don't have permission to approve purchase orders.</div>
        </div>
      </div>
    );
  }

  const columns = [
    {
      title: 'Req #',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id) => <Text type="secondary">#{id}</Text>,
    },
    {
      title: 'PO #',
      dataIndex: 'purchase_order_id',
      key: 'po_id',
      width: 70,
      render: (id) => <Text strong style={{ color: '#1890ff' }}>#{id}</Text>,
    },
    {
      title: 'Firm / Vendor',
      key: 'vendor',
      width: 170,
      ellipsis: true,
      render: (_, r) => {
        const vendor = r.purchase_order?.vendor_name || '—';
        const firm = r.purchase_order?.firm_name || '—';
        return (
          <div style={{ lineHeight: 1.4 }}>
            <Tooltip title={firm}><div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{firm}</div></Tooltip>
            <Tooltip title={vendor}><div style={{ color: '#595959', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vendor}</div></Tooltip>
          </div>
        );
      },
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (_, r) => (
        <Text strong style={{ color: '#1890ff', fontSize: 14 }}>
          {fmtMoney(r.purchase_order?.total_amount)}
        </Text>
      ),
      sorter: (a, b) =>
        (a.purchase_order?.total_amount || 0) - (b.purchase_order?.total_amount || 0),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Requested By',
      key: 'requested_by',
      width: 130,
      render: (_, r) => r.requested_by_name || '—',
    },
    {
      title: 'Requested At',
      dataIndex: 'requested_at',
      key: 'requested_at',
      width: 160,
      render: fmtDate,
      sorter: (a, b) => new Date(a.requested_at) - new Date(b.requested_at),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: getApprovalStatusTag,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 190,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() =>
              setApproveModal({
                poId: record.purchase_order_id,
                poNumber: record.purchase_order_id,
                amount: record.purchase_order?.total_amount,
                vendor: record.purchase_order?.vendor_name,
              })
            }
            loading={actionLoading}
          >
            Approve
          </Button>
          <Button
            danger
            size="small"
            icon={<CloseCircleOutlined />}
            onClick={() =>
              setRejectModal({
                poId: record.purchase_order_id,
                poNumber: record.purchase_order_id,
                amount: record.purchase_order?.total_amount,
                vendor: record.purchase_order?.vendor_name,
              })
            }
            loading={actionLoading}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-content">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><CheckSquareOutlined /></div>
            <div className="page-header-text">
              <h1>Approval Inbox</h1>
              <p>Purchase orders waiting for your approval or rejection</p>
            </div>
          </div>
        </div>

        {/* ── Stats ───────────────────────────────────────────────────── */}
        <div className="stats-grid">
          <div className="stat-card" style={{ borderLeftColor: pendingApprovals.length > 0 ? '#faad14' : '#52c41a' }}>
            <div className="stat-icon" style={{ background: pendingApprovals.length > 0 ? '#fffbe6' : '#f6ffed', color: pendingApprovals.length > 0 ? '#faad14' : '#52c41a' }}><ClockCircleOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: pendingApprovals.length > 0 ? '#faad14' : '#52c41a' }}>{pendingApprovals.length}</div>
              <div className="stat-label">Pending Approvals</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#fa8c16' }}>
            <div className="stat-icon" style={{ background: '#fff7e6', color: '#fa8c16' }}><DollarOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#fa8c16' }}>
                ₹{Number(pendingApprovals.reduce((s, r) => s + (r.purchase_order?.total_amount || 0), 0)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </div>
              <div className="stat-label">Pending Amount</div>
            </div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#1890ff' }}>
            <div className="stat-icon" style={{ background: '#e6f4ff', color: '#1890ff' }}><TeamOutlined /></div>
            <div className="stat-content">
              <div className="stat-value" style={{ color: '#1890ff' }}>{new Set(pendingApprovals.map((r) => r.purchase_order?.vendor_name)).size}</div>
              <div className="stat-label">Vendors Waiting</div>
            </div>
          </div>
        </div>

        {error && (
          <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} closable />
        )}

        {pendingApprovals.length === 0 && !loading && (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message="All clear — no purchase orders pending approval."
            style={{ marginBottom: 16 }}
          />
        )}

        {/* ── Table ───────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>
            Pending Approvals
            {pendingApprovals.length > 0 && (
              <Badge count={pendingApprovals.length} style={{ marginLeft: 8 }} />
            )}
          </Title>
          <Button icon={<ReloadOutlined />} onClick={() => dispatch(fetchPendingApprovalsRequest())} loading={loading}>
            Refresh
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={pendingApprovals}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: false }}
          rowClassName={() => 'approval-pending-row'}
          locale={{
            emptyText: (
              <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
                No pending approvals. All purchase orders are proceeding normally.
              </div>
            ),
          }}
        />
      </div>

      {/* ── Approve Modal ────────────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            Approve Purchase Order #{approveModal?.poNumber}
          </Space>
        }
        open={!!approveModal}
        onCancel={() => { setApproveModal(null); setApproveRemarks(''); }}
        footer={
          <Space>
            <Button onClick={() => { setApproveModal(null); setApproveRemarks(''); }}>Cancel</Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={actionLoading}
              onClick={handleApprove}
            >
              Approve
            </Button>
          </Space>
        }
        destroyOnClose
      >
        {approveModal && (
          <>
            <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Vendor">{approveModal.vendor}</Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                <Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                  {fmtMoney(approveModal.amount)}
                </Text>
              </Descriptions.Item>
            </Descriptions>
            <Alert
              type="info"
              showIcon
              message="Approving will change the PO status to Open — normal delivery flow will resume."
              style={{ marginBottom: 12 }}
            />
            <Input.TextArea
              rows={3}
              placeholder="Optional approval remarks (e.g. Approved — within Q2 budget allocation)"
              value={approveRemarks}
              onChange={(e) => setApproveRemarks(e.target.value)}
              maxLength={500}
              showCount
            />
          </>
        )}
      </Modal>

      {/* ── Reject Modal ─────────────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            Reject Approval — PO #{rejectModal?.poNumber}
          </Space>
        }
        open={!!rejectModal}
        onCancel={() => { setRejectModal(null); setRejectReason(''); setRejectError(''); }}
        footer={
          <Space>
            <Button onClick={() => { setRejectModal(null); setRejectReason(''); setRejectError(''); }}>Cancel</Button>
            <Button
              danger
              icon={<CloseCircleOutlined />}
              loading={actionLoading}
              onClick={handleReject}
            >
              Reject
            </Button>
          </Space>
        }
        destroyOnClose
      >
        {rejectModal && (
          <>
            <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Vendor">{rejectModal.vendor}</Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                <Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                  {fmtMoney(rejectModal.amount)}
                </Text>
              </Descriptions.Item>
            </Descriptions>
            <Alert
              type="warning"
              showIcon
              message="Rejecting will cancel the PO. This cannot be undone."
              style={{ marginBottom: 12 }}
            />
            <TextArea
              rows={3}
              placeholder="Rejection reason (required) — explain why this PO is being rejected"
              value={rejectReason}
              onChange={(e) => { setRejectReason(e.target.value); if (e.target.value.trim()) setRejectError(''); }}
              maxLength={1000}
              showCount
              status={rejectError ? 'error' : ''}
            />
            {rejectError && (
              <Text type="danger" style={{ fontSize: 12 }}>{rejectError}</Text>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default ApprovalInbox;
